import * as React from 'react';
import { useEffect, useState } from 'react';
import moment from 'moment-timezone';
import { eventDay } from '../../lib/eventStats';
import { toast } from 'sonner';
import SettingsCard from '../../components/SettingsCard';
import { useDialogRef, dialogPanelProps } from '../../components/a11y';
import useLicenseTypeManagement from '@/src/Licenses/components/hooks/useLicenseTypeManagement';
import useFeatureFlagManagement from '@/src/Features/components/hooks/useFeatureFlagManagement';
import useOrgs from '@/src/Orgs/components/hooks/useOrgs';

/*
 * Licenses admin — license types (with their feature grants) and the
 * licenses issued to orgs. Classic split this across six views with an
 * N+1 fetch loop and an unwired delete; here it's two cards.
 */

interface LicenseTypeRow {
    id: number;
    name: string;
    adminName: string;
    description: string | null;
    simultaneousDeviceCount: number;
}

interface LicenseRow {
    id: number;
    key: string;
    licenseTypeName?: string;
    licenseTypeId: number;
    expiresAt: string | null;
    expiredAt: string | null;
    createdAt: string;
}

const FIELD_LABEL =
    'mb-1.5 block text-[12px] font-semibold text-muted-foreground';

const EMPTY_TYPE = {
    name: '',
    adminName: '',
    description: '',
    simultaneousDeviceCount: 5,
};

export default function AdminLicensesV2() {
    const {
        fetchLicenseTypes,
        createLicenseType,
        updateLicenseType,
        deleteLicenseType,
        listFeaturesForLicenseType,
        addFeatureToLicenseType,
        removeFeatureFromLicenseType,
        getLicensesForOrg,
        createLicense,
        expireLicense,
    } = useLicenseTypeManagement();
    const { fetchFeatureFlags } = useFeatureFlagManagement();
    const { listOrgs } = useOrgs();

    const [types, setTypes] = useState<LicenseTypeRow[]>([]);
    const [orgs, setOrgs] = useState<any[]>([]);
    const [orgFilter, setOrgFilter] = useState<string>('');
    const [licenses, setLicenses] = useState<LicenseRow[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [licensesLoading, setLicensesLoading] = useState(false);

    const [typeDialog, setTypeDialog] = useState<
        { kind: 'create' } | { kind: 'edit'; row: LicenseTypeRow } | null
    >(null);
    const [typeDraft, setTypeDraft] = useState(EMPTY_TYPE);
    const [featureDialog, setFeatureDialog] =
        useState<LicenseTypeRow | null>(null);
    const [allFlags, setAllFlags] = useState<any[]>([]);
    const [grantedIds, setGrantedIds] = useState<Set<number>>(new Set());
    const [licenseDialog, setLicenseDialog] = useState(false);
    const [licenseDraft, setLicenseDraft] = useState({
        orgId: '',
        licenseTypeId: '',
        expiresAt: '',
    });
    const [busy, setBusy] = useState(false);
    const [dialogError, setDialogError] = useState<string | null>(null);

    const anyDialog =
        typeDialog != null || featureDialog != null || licenseDialog;
    const closeDialogs = () => {
        setTypeDialog(null);
        setFeatureDialog(null);
        setLicenseDialog(false);
    };
    const dialogRef = useDialogRef(anyDialog, closeDialogs);

    useEffect(() => {
        let mounted = true;
        Promise.all([
            fetchLicenseTypes().catch(() => []),
            listOrgs().catch(() => []),
        ]).then(([typeRows, orgRows]) => {
            if (!mounted) return;
            setTypes(typeRows ?? []);
            setOrgs(
                [...(orgRows ?? [])].sort((a: any, b: any) =>
                    (a.name ?? '').localeCompare(b.name ?? ''),
                ),
            );
            setIsLoading(false);
        });
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!orgFilter) {
            setLicenses([]);
            return;
        }
        let mounted = true;
        setLicensesLoading(true);
        getLicensesForOrg(+orgFilter)
            .catch(() => [])
            .then((rows) => {
                if (!mounted) return;
                setLicenses(rows ?? []);
                setLicensesLoading(false);
            });
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orgFilter]);

    /* ---- license types ---- */

    const openTypeCreate = () => {
        setTypeDraft(EMPTY_TYPE);
        setDialogError(null);
        setTypeDialog({ kind: 'create' });
    };

    const openTypeEdit = (row: LicenseTypeRow) => {
        setTypeDraft({
            name: row.name ?? '',
            adminName: row.adminName ?? '',
            description: row.description ?? '',
            simultaneousDeviceCount: row.simultaneousDeviceCount ?? 5,
        });
        setDialogError(null);
        setTypeDialog({ kind: 'edit', row });
    };

    const submitType = async () => {
        if (!typeDialog || busy) return;
        if (!typeDraft.name.trim() || !typeDraft.adminName.trim()) {
            setDialogError('Name and admin name are required.');
            return;
        }
        setBusy(true);
        setDialogError(null);
        const body = {
            name: typeDraft.name.trim(),
            adminName: typeDraft.adminName.trim(),
            description: typeDraft.description.trim(),
            simultaneousDeviceCount: Number(
                typeDraft.simultaneousDeviceCount,
            ),
        };
        const ok =
            typeDialog.kind === 'create'
                ? await createLicenseType(body).catch(() => null)
                : await updateLicenseType(typeDialog.row.id, body).catch(
                      () => null,
                  );
        setBusy(false);
        if (ok) {
            setTypeDialog(null);
            toast.success(
                typeDialog.kind === 'create'
                    ? 'License type created'
                    : 'Saved',
            );
            setTypes((await fetchLicenseTypes().catch(() => [])) ?? []);
        } else {
            setDialogError("Couldn't save — try again.");
        }
    };

    const removeType = async (row: LicenseTypeRow) => {
        if (
            !window.confirm(
                `Delete the "${row.name}" license type? Existing org licenses of this type keep working.`,
            )
        )
            return;
        const prev = types;
        setTypes((current) => current.filter((t) => t.id !== row.id));
        const ok = await deleteLicenseType(row.id)
            .then(() => true)
            .catch(() => false);
        if (!ok) {
            setTypes(prev);
            toast.error(
                "Couldn't delete the type — it may be in use by a license.",
            );
        }
    };

    /* ---- feature grants ---- */

    const openFeatures = async (row: LicenseTypeRow) => {
        setFeatureDialog(row);
        setGrantedIds(new Set());
        const [flags, granted] = await Promise.all([
            allFlags.length > 0
                ? Promise.resolve(allFlags)
                : fetchFeatureFlags().catch(() => []),
            listFeaturesForLicenseType(row.id).catch(() => []),
        ]);
        setAllFlags(flags ?? []);
        setGrantedIds(
            new Set((granted ?? []).map((g: any) => g.featureId)),
        );
    };

    const toggleFeature = async (flag: any) => {
        if (!featureDialog) return;
        const has = grantedIds.has(flag.id);
        setGrantedIds((current) => {
            const next = new Set(current);
            if (has) next.delete(flag.id);
            else next.add(flag.id);
            return next;
        });
        const ok = has
            ? await removeFeatureFromLicenseType(featureDialog.id, flag.id)
                  .then(() => true)
                  .catch(() => false)
            : await addFeatureToLicenseType(featureDialog.id, flag.id)
                  .then(() => true)
                  .catch(() => false);
        if (!ok) {
            setGrantedIds((current) => {
                const next = new Set(current);
                if (has) next.add(flag.id);
                else next.delete(flag.id);
                return next;
            });
            toast.error("Couldn't update the grant.");
        }
    };

    /* ---- org licenses ---- */

    const submitLicense = async () => {
        if (busy || !licenseDraft.orgId || !licenseDraft.licenseTypeId)
            return;
        setBusy(true);
        setDialogError(null);
        const ok = await createLicense({
            orgId: +licenseDraft.orgId,
            licenseTypeId: +licenseDraft.licenseTypeId,
            expiresAt: licenseDraft.expiresAt || null,
        }).catch(() => null);
        setBusy(false);
        if (ok) {
            setLicenseDialog(false);
            toast.success('License created');
            if (orgFilter === licenseDraft.orgId) {
                setLicenses(
                    (await getLicensesForOrg(+orgFilter).catch(() => [])) ??
                        [],
                );
            } else {
                setOrgFilter(licenseDraft.orgId);
            }
        } else {
            setDialogError("Couldn't create the license.");
        }
    };

    const expire = async (row: LicenseRow) => {
        if (
            !window.confirm(
                'Expire this license? Devices on it stop authenticating.',
            )
        )
            return;
        const ok = await expireLicense(row.id)
            .then(() => true)
            .catch(() => false);
        if (ok) {
            toast.success('License expired');
            setLicenses(
                (await getLicensesForOrg(+orgFilter).catch(() => [])) ?? [],
            );
        } else {
            toast.error("Couldn't expire the license.");
        }
    };

    if (isLoading) {
        return (
            <div className="mb-6 h-40 animate-pulse rounded-[16px] bg-secondary" />
        );
    }

    const dialogShell = (label: string, children: React.ReactNode) => (
        <div
            className="psv2 fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-6"
            onClick={closeDialogs}
        >
            <div
                ref={dialogRef}
                {...dialogPanelProps(label)}
                className="w-full max-w-[420px] rounded-[15px] border border-border bg-background p-5 shadow-xl outline-none"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="ps-label mb-3.5">{label}</div>
                {children}
            </div>
        </div>
    );

    return (
        <>
            <SettingsCard
                title="License types"
                desc="What a license grants — device limit and feature access."
                foot={
                    <>
                        <span className="flex-1 text-[11.5px] text-faint">
                            {types.length} type
                            {types.length === 1 ? '' : 's'}
                        </span>
                        <button
                            type="button"
                            onClick={openTypeCreate}
                            className="cursor-pointer rounded-full border-0 bg-foreground px-[18px] py-2 font-[inherit] text-[12.5px] font-semibold text-background"
                        >
                            Add type
                        </button>
                    </>
                }
            >
                {types.length === 0 && (
                    <p className="py-2 text-[13px] text-muted-foreground">
                        No license types yet.
                    </p>
                )}
                {types.map((row) => (
                    <div
                        key={row.id}
                        className="group flex items-center gap-3 border-b border-line py-[9px] text-[12.5px] last:border-b-0"
                    >
                        <span className="w-[150px] flex-none truncate font-semibold">
                            {row.name}
                        </span>
                        <span className="min-w-0 flex-1 truncate text-muted-foreground">
                            {row.adminName}
                        </span>
                        <span className="whitespace-nowrap font-mono text-[10px] text-faint">
                            {row.simultaneousDeviceCount} DEVICES
                        </span>
                        <span className="flex gap-3 text-[12px] font-semibold opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
                            <button
                                type="button"
                                onClick={() => openTypeEdit(row)}
                                className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-muted-foreground hover:text-foreground"
                            >
                                edit
                            </button>
                            <button
                                type="button"
                                onClick={() => openFeatures(row)}
                                className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-muted-foreground hover:text-foreground"
                            >
                                features
                            </button>
                            <button
                                type="button"
                                title="Delete type"
                                onClick={() => removeType(row)}
                                className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-faint hover:text-attention"
                            >
                                ×
                            </button>
                        </span>
                    </div>
                ))}
            </SettingsCard>

            <SettingsCard
                title="Org licenses"
                desc="Licenses issued to organizations. Pick an org to see its keys."
                foot={
                    <>
                        <span className="flex-1 text-[11.5px] text-faint">
                            expiring a license stops its devices
                        </span>
                        <button
                            type="button"
                            onClick={() => {
                                setLicenseDraft({
                                    orgId: orgFilter,
                                    licenseTypeId: '',
                                    expiresAt: '',
                                });
                                setDialogError(null);
                                setLicenseDialog(true);
                            }}
                            className="cursor-pointer rounded-full border-0 bg-foreground px-[18px] py-2 font-[inherit] text-[12.5px] font-semibold text-background"
                        >
                            Create license
                        </button>
                    </>
                }
            >
                <select
                    className="ps-input mb-2 w-[240px] cursor-pointer appearance-none text-[12.5px]"
                    value={orgFilter}
                    onChange={(e) => setOrgFilter(e.target.value)}
                >
                    <option value="">Pick an organization…</option>
                    {orgs.map((o: any) => (
                        <option key={o.id} value={o.id}>
                            {o.name}
                        </option>
                    ))}
                </select>
                {licensesLoading && (
                    <div className="h-16 animate-pulse rounded-[10px] bg-secondary" />
                )}
                {!licensesLoading &&
                    orgFilter &&
                    licenses.length === 0 && (
                        <p className="py-2 text-[13px] text-muted-foreground">
                            No licenses for this org yet.
                        </p>
                    )}
                {!licensesLoading &&
                    licenses.map((row) => {
                        const expired =
                            !!row.expiredAt ||
                            (row.expiresAt &&
                                eventDay(row.expiresAt)
                                    .endOf('day')
                                    .isBefore(moment()));
                        return (
                            <div
                                key={row.id}
                                className="group flex items-center gap-3 border-b border-line py-[9px] text-[12.5px] last:border-b-0"
                            >
                                <span className="w-[130px] flex-none truncate font-semibold">
                                    {row.licenseTypeName ??
                                        types.find(
                                            (t) =>
                                                t.id === row.licenseTypeId,
                                        )?.name ??
                                        '—'}
                                </span>
                                <span className="min-w-0 flex-1 truncate font-mono text-[10px] text-faint">
                                    {row.key}
                                </span>
                                <span
                                    className={
                                        'whitespace-nowrap text-[11px] font-semibold ' +
                                        (expired
                                            ? 'text-attention'
                                            : 'text-good')
                                    }
                                >
                                    {expired
                                        ? 'expired'
                                        : row.expiresAt
                                        ? `until ${eventDay(
                                              row.expiresAt,
                                          ).format('MMM D, YYYY')}`
                                        : 'active'}
                                </span>
                                {!expired && (
                                    <button
                                        type="button"
                                        onClick={() => expire(row)}
                                        className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12px] font-semibold text-faint opacity-0 transition-opacity hover:text-attention focus-visible:opacity-100 group-hover:opacity-100"
                                    >
                                        expire
                                    </button>
                                )}
                            </div>
                        );
                    })}
            </SettingsCard>

            {typeDialog &&
                dialogShell(
                    typeDialog.kind === 'create'
                        ? 'Add license type'
                        : `Edit ${typeDialog.row.name}`,
                    <>
                        <div className="mb-3 grid grid-cols-2 gap-3">
                            <div>
                                <label className={FIELD_LABEL}>Name</label>
                                <input
                                    autoFocus
                                    className="ps-input w-full text-[13px]"
                                    value={typeDraft.name}
                                    onChange={(e) =>
                                        setTypeDraft((d) => ({
                                            ...d,
                                            name: e.target.value,
                                        }))
                                    }
                                />
                            </div>
                            <div>
                                <label className={FIELD_LABEL}>
                                    Admin name
                                </label>
                                <input
                                    className="ps-input w-full text-[13px]"
                                    value={typeDraft.adminName}
                                    onChange={(e) =>
                                        setTypeDraft((d) => ({
                                            ...d,
                                            adminName: e.target.value,
                                        }))
                                    }
                                />
                            </div>
                        </div>
                        <label className={FIELD_LABEL}>Description</label>
                        <textarea
                            className="ps-input mb-3 h-[64px] w-full resize-y text-[13px]"
                            value={typeDraft.description}
                            onChange={(e) =>
                                setTypeDraft((d) => ({
                                    ...d,
                                    description: e.target.value,
                                }))
                            }
                        />
                        <label className={FIELD_LABEL}>Device limit</label>
                        <input
                            type="number"
                            min={1}
                            className="ps-input mb-4 w-[120px] text-[13px] tabular-nums"
                            value={typeDraft.simultaneousDeviceCount}
                            onChange={(e) =>
                                setTypeDraft((d) => ({
                                    ...d,
                                    simultaneousDeviceCount: +e.target
                                        .value,
                                }))
                            }
                        />
                        <div className="flex items-center gap-3">
                            <button
                                type="button"
                                disabled={busy}
                                onClick={submitType}
                                className="cursor-pointer rounded-full border-0 bg-brand px-5 py-2 font-[inherit] text-[13px] font-semibold text-brand-foreground disabled:opacity-50"
                            >
                                {busy ? 'Saving…' : 'Save'}
                            </button>
                            <button
                                type="button"
                                onClick={closeDialogs}
                                className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12.5px] font-semibold text-muted-foreground"
                            >
                                Cancel
                            </button>
                        </div>
                        {dialogError && (
                            <p className="mt-2.5 text-[12px] font-semibold text-attention">
                                {dialogError}
                            </p>
                        )}
                    </>,
                )}

            {featureDialog &&
                dialogShell(
                    `Features — ${featureDialog.name}`,
                    <>
                        <p className="mb-2 text-[12px] text-faint">
                            Toggles apply immediately.
                        </p>
                        <div className="max-h-[320px] overflow-y-auto">
                            {allFlags.length === 0 && (
                                <div className="h-16 animate-pulse rounded-[10px] bg-secondary" />
                            )}
                            {allFlags.map((flag: any) => (
                                <label
                                    key={flag.id}
                                    className="flex cursor-pointer items-center gap-2.5 rounded-[8px] px-1.5 py-[7px] text-[12.5px] hover:bg-secondary"
                                >
                                    <input
                                        type="checkbox"
                                        checked={grantedIds.has(flag.id)}
                                        onChange={() =>
                                            toggleFeature(flag)
                                        }
                                    />
                                    <span className="font-mono text-[11px]">
                                        {flag.key}
                                    </span>
                                </label>
                            ))}
                        </div>
                        <button
                            type="button"
                            onClick={closeDialogs}
                            className="mt-3 cursor-pointer rounded-full border-0 bg-foreground px-5 py-2 font-[inherit] text-[12.5px] font-semibold text-background"
                        >
                            Done
                        </button>
                    </>,
                )}

            {licenseDialog &&
                dialogShell(
                    'Create license',
                    <>
                        <label className={FIELD_LABEL}>Organization</label>
                        <select
                            autoFocus
                            className="ps-input mb-3 w-full cursor-pointer appearance-none text-[13px]"
                            value={licenseDraft.orgId}
                            onChange={(e) =>
                                setLicenseDraft((d) => ({
                                    ...d,
                                    orgId: e.target.value,
                                }))
                            }
                        >
                            <option value="">Pick an organization…</option>
                            {orgs.map((o: any) => (
                                <option key={o.id} value={o.id}>
                                    {o.name}
                                </option>
                            ))}
                        </select>
                        <label className={FIELD_LABEL}>License type</label>
                        <select
                            className="ps-input mb-3 w-full cursor-pointer appearance-none text-[13px]"
                            value={licenseDraft.licenseTypeId}
                            onChange={(e) =>
                                setLicenseDraft((d) => ({
                                    ...d,
                                    licenseTypeId: e.target.value,
                                }))
                            }
                        >
                            <option value="">Pick a type…</option>
                            {types.map((t) => (
                                <option key={t.id} value={t.id}>
                                    {t.name}
                                </option>
                            ))}
                        </select>
                        <label className={FIELD_LABEL}>
                            Expires{' '}
                            <span className="font-normal text-faint">
                                — optional
                            </span>
                        </label>
                        <input
                            type="date"
                            className="ps-input mb-4 text-[13px]"
                            value={licenseDraft.expiresAt}
                            onChange={(e) =>
                                setLicenseDraft((d) => ({
                                    ...d,
                                    expiresAt: e.target.value,
                                }))
                            }
                        />
                        <div className="flex items-center gap-3">
                            <button
                                type="button"
                                disabled={
                                    busy ||
                                    !licenseDraft.orgId ||
                                    !licenseDraft.licenseTypeId
                                }
                                onClick={submitLicense}
                                className="cursor-pointer rounded-full border-0 bg-brand px-5 py-2 font-[inherit] text-[13px] font-semibold text-brand-foreground disabled:opacity-50"
                            >
                                {busy ? 'Creating…' : 'Create license'}
                            </button>
                            <button
                                type="button"
                                onClick={closeDialogs}
                                className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12.5px] font-semibold text-muted-foreground"
                            >
                                Cancel
                            </button>
                        </div>
                        {dialogError && (
                            <p className="mt-2.5 text-[12px] font-semibold text-attention">
                                {dialogError}
                            </p>
                        )}
                    </>,
                )}
        </>
    );
}
